import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { Subscription, combineLatest, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { LoginResponse } from 'angular-auth-oidc-client';
import { OidcService } from './core/services/oidc.service';
import { EnvironmentService } from './core/services/environment.service';
import { Constants } from './app.constants';
import { AppConfigService } from './core/services/app-config.service';
import { AuthService } from './core/services/auth.service';
import { PageVisibilityService } from './core/services/page-visibility.service';
import { UserService } from './core/services/user.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'star-educator-reports-shell';
  private subscriptions = new Subscription();

  constructor(
    private oidcService: OidcService,
    private environmentService: EnvironmentService,
    private router: Router,
    private appConfigService: AppConfigService,
    private authService: AuthService,
    private pageVisibilityService: PageVisibilityService,
    private userService: UserService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    // Skip: the OIDC front-channel logout iframe also boots this app and would
    // otherwise race the visible tab for 'user-logout-pending'.
    if (this.isInHiddenIframe()) {
      return;
    }

    // Start monitoring page visibility for token expiration detection
    this.pageVisibilityService.startMonitoring();

    // Check if logout just completed
    const logoutPending = localStorage.getItem('user-logout-pending');
    if (logoutPending === 'true') {
      // Remove the logout flag
      localStorage.removeItem('user-logout-pending');

      // Clear ALL storage using centralized method
      this.oidcService.clearAuthStorage();

      // Clear cached user details so the next user doesn't see stale data
      this.userService.clearCache();

      // Ensure auth state is completely cleared
      this.oidcService.isAuthenticated$.next(false);
      this.oidcService.userData$.next(undefined);
      this.oidcService.accessToken$.next('');
      this.oidcService.checkAuthDone$.next(true); // Mark as done so guards proceed

      // Guards will now see isAuthenticated = false and redirect to login
      return;
    }

    const pathname = this.document.location.pathname;
    if (!pathname.includes('/logout') && !pathname.includes('/callback')) {
      // Initialize mock auth if enabled
      if (this.appConfigService.config.featureFlags.enableMockAuth) {
        this.authService.initiateLogin('/educator/summary-dce');
        this.oidcService.isAuthenticated$.next(true);
        this.oidcService.checkAuthDone$.next(true);
      } else {
        this.checkAuth();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.pageVisibilityService.stopMonitoring();
  }

  private checkAuth() {
    this.subscriptions.add(
      this.environmentService.configId$
        .pipe(
          filter((configId: string | undefined) => !!configId),
          switchMap((configId: string | undefined) => this.oidcService.checkAuth(configId))
        )
        .subscribe({
          next: (loginResponse: LoginResponse) => {
            if (loginResponse.isAuthenticated) {
              // Temporarily disabled - was causing errors
              // this.forceRefreshSession();

              const redirectUrl = sessionStorage.getItem(Constants.WEB_STORAGE_KEY.REDIRECT);
              if (redirectUrl && this.isValidRedirectUrl(redirectUrl)) {
                sessionStorage.removeItem(Constants.WEB_STORAGE_KEY.REDIRECT);
                this.router.navigateByUrl(redirectUrl, { replaceUrl: false });
              } else if (redirectUrl) {
                console.warn('[AppComponent] Invalid redirect URL detected:', redirectUrl);
                sessionStorage.removeItem(Constants.WEB_STORAGE_KEY.REDIRECT);
              }
            }
          },
          error: (error: any) => {
            console.error('[AppComponent] checkAuth ERROR:', error);
            // Still mark as done so guards can proceed
            this.oidcService.checkAuthDone$.next(true);
          }
        })
    );
  }

  private isInHiddenIframe(): boolean {
    return window.self !== window.top;
  }

  private forceRefreshSession() {
    this.subscriptions.add(
      combineLatest([
        this.oidcService.isAuthenticated$,
        this.oidcService.checkAuthDone$,
        this.oidcService.forceRefreshSessionDone$
      ])
        .pipe(
          map(([isAuthenticated, checkAuthDone, forceRefreshSessionDone]: [boolean, boolean, boolean]) => {
            return !!isAuthenticated && !!checkAuthDone && !forceRefreshSessionDone;
          }),
          distinctUntilChanged(),
          filter((shouldRefresh: boolean) => shouldRefresh)
        )
        .subscribe(() => {
          this.subscriptions.add(
            this.oidcService.forceRefreshSession()
              .subscribe({
                error: (error: any) => {
                  console.error(`[forceRefreshSession] ERROR - ${JSON.stringify(error)}`);
                }
              })
          );
        })
    );
  }

  /**
   * Validates that the redirect URL is safe (relative path only)
   * Prevents open redirect attacks by rejecting absolute URLs
   */
  private isValidRedirectUrl(url: string): boolean {
    if (!url) {
      return false;
    }

    // Must start with / (relative path)
    if (!url.startsWith('/')) {
      return false;
    }

    // Reject protocol-relative URLs (//example.com)
    if (url.startsWith('//')) {
      return false;
    }

    // Reject URLs with protocols (http://, javascript:, data:, etc.)
    if (url.includes(':')) {
      return false;
    }

    return true;
  }

}
